import { BaseDestroy } from 'nera-common/angular';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoModule } from '@jsverse/transloco';
import { SubjectConsentsService } from 'app/features/photoqa/services/subject-consents.service';
import { marketingSiteUrl } from '../marketing-site-url';

interface IConsentGateDialogData {
    /** Server policy version both acknowledgements are recorded under. */
    policyVersion: string;
}

/**
 * Non-dismissable Terms + AI-processing acceptance dialog — the fe-web half of
 * kanban #3306 / PFL-2569 MR8, mirroring iam-portal's `ConsentGate` (same copy,
 * same single `PUT /me/consents/acceptance` call). The Terms/Privacy links open
 * the marketing site; the CTA stays disabled until both boxes are ticked.
 */
@Component({
    selector: 'app-consent-gate-dialog',
    imports: [
        ReactiveFormsModule,
        MatButtonModule,
        MatCheckboxModule,
        MatDialogModule,
        MatProgressSpinnerModule,
        TranslocoModule,
    ],
    templateUrl: './consent-gate-dialog.component.html',
})
export class ConsentGateDialogComponent extends BaseDestroy {
    private readonly _fb = inject(FormBuilder);
    private readonly _consents = inject(SubjectConsentsService);
    private readonly _ref = inject(
        MatDialogRef<ConsentGateDialogComponent>
    );
    private readonly _data = inject<IConsentGateDialogData>(MAT_DIALOG_DATA);

    readonly marketingBase = marketingSiteUrl();

    readonly form = this._fb.group({
        terms: [false, Validators.requiredTrue],
        aiProcessing: [false, Validators.requiredTrue],
    });

    accepting = false;
    failed = false;

    accept(): void {
        if (this.form.invalid || this.accepting) return;
        this.accepting = true;
        this.failed = false;

        this._consents
            .recordSignupAcceptance(this._data.policyVersion)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => this._ref.close(true),
                // Stale policy version (CONSENT_POLICY_VERSION_OUTDATED) or a
                // transport failure: show the error line and let the user retry —
                // next session re-evaluates and re-prompts regardless.
                error: () => {
                    this.accepting = false;
                    this.failed = true;
                },
            });
    }
}
